import { forwardRef, type ReactNode } from 'react';

import { DataTestId } from '../../core/types/data-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { canReceiveDefaultTextFormat as _canReceiveDefaultTextFormat } from '../../core/utils/can-receive-default-text-format.js';
import { Text } from '../../typography/text/Text.js';

//#region Typings
/**
 * Accepted properties for the Avatar Label
 * @public
 */
export interface AvatarLabelProps extends StylingProps, DataTestId {
  /** Elements to be displayed in the Avatar Label slot. */
  children: ReactNode;
}
//#endregion

/**
 * Avatar Label component
 * @public
 */
export const Label = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  AvatarLabelProps
>((props, forwardedRef) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
  }: AvatarLabelProps = props;
  const childIsText = _canReceiveDefaultTextFormat(children);

  return childIsText ? (
    <Text
      as="div"
      textStyle="base"
      ref={forwardedRef}
      className={consumerClassName}
      style={consumerStyle}
      data-testid={dataTestId}
    >
      {children}
    </Text>
  ) : (
    <div
      data-testid={dataTestId}
      ref={forwardedRef}
      className={consumerClassName}
      style={consumerStyle}
    >
      {children}
    </div>
  );
});

(Label as typeof Label & { displayName: string }).displayName = 'Avatar.Label';
